import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useClient } from 'urql';

import {
  CreateOrderPaymentIntentDocument,
  type CreateOrderPaymentIntentMutation,
  type CreateOrderPaymentIntentMutationVariables,
} from '../../GraphQL/CreatePaymentIntent.generated';

/**
 * Creates a payment intent for the provided order.
 */
export const useCreatePaymentIntent = () => {
  const urqlClient = useClient();
  const { formatMessage } = useIntl();

  return useCallback(
    async (input: CreateOrderPaymentIntentMutationVariables['input']) => {
      const response = await urqlClient
        .mutation<CreateOrderPaymentIntentMutation>(
          CreateOrderPaymentIntentDocument,
          { input },
          { requestPolicy: 'network-only' },
        )
        .toPromise();

      const paymentIntentId = response.data?.createOrderPaymentIntent?.id;
      const clientSecret = response.data?.createOrderPaymentIntent?.secret;

      if (!paymentIntentId || !clientSecret) {
        // Caught by the Stripe integration and shown to the customer.
        throw new Error(formatMessage(messages.error));
      }

      return {
        paymentIntentId,
        clientSecret,
      };
    },
    [urqlClient, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong setting up your payment.',
    description: 'Stripe Payment | Create Payment Intent Error',
  },
});
